import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { PlaceNameSource, SpeedLimitSource, TileSource } from '@/enums/mapSource'
import { TILE_URL_TEMPLATE } from '@/libs/tiles'
import { useAppStore } from '@/stores/app'
import { API_BASE_URL, useFetchStore } from '@/stores/fetch'
import type { IMapSources } from '@/types/MapSource'

/*
 * Where the map is drawn from — one answer for the whole console.
 *
 * THE DEFAULTS ARE TODAY'S CONSOLE. Everything below starts at OpenStreetMap with no
 * traffic and no token, which is exactly what the console did before this store
 * existed, and load() only ever moves away from that on a 200. A deployment whose
 * stanic-api does not know /map-sources yet answers 404, the failure is swallowed, and
 * the map keeps drawing OSM tiles straight from tile.openstreetmap.org. That is the
 * point: this store can only add a source, never take the map away.
 *
 * THE KEY IS NOT HERE and never will be. What arrives is a short-lived, opaque tile
 * token that lets THIS browser ask stanic-api for tiles; stanic-api holds the TomTom key
 * and does the fetching. So the token goes in a query string (an <img> carries no
 * Authorization header) and is worth only what it can fetch, for as long as it lasts.
 */
const defaultSources = (): IMapSources => ({
    tiles:                 TileSource.OSM,
    traffic:               false,
    place_names:           PlaceNameSource.Nominatim,
    speed_limits:          SpeedLimitSource.OSM,
    tomtom_key_set:        false,
    tile_token:            '',
    tile_token_expires_at: '',
})

/*
 * How long before the token expires the console asks for a new one, and the floor
 * under that wait.
 *
 * A console is left open on a wall overnight; a token that quietly expires at three in
 * the morning would leave somebody an empty grey box in the morning with nothing on
 * screen to say why. So the reload is scheduled rather than waited for — a minute of
 * margin covers a slow answer, and the floor keeps a token that arrives already nearly
 * spent from turning into a request loop.
 */
const RENEW_MARGIN_MS = 60_000
const MIN_RENEW_MS    = 30_000

/*
 * How often the traffic tiles are asked for again.
 *
 * Traffic is the one layer whose answer goes stale while nobody touches anything: the
 * road that was green two minutes ago is the reason to look at this map at all. The
 * bucket is part of the tile's address, so changing it is what makes the browser —
 * which is caching these correctly and deliberately — go and ask again.
 */
const TRAFFIC_BUCKET_MS = 120_000

/*
 * TomTom ships a real night map, so dark mode asks for it instead of inverting a light
 * one in the browser the way the OSM tiles have to be (assets/main.css, .map-tiles).
 */
const tileStyle = (dark: boolean): string => dark ? 'night' : 'main'

export const useMapSourcesStore = defineStore('mapSources', () => {
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    const sources = ref<IMapSources>(defaultSources())

    /** Changes every TRAFFIC_BUCKET_MS; only ever read into a traffic tile's address. */
    const trafficBucket = ref(Math.floor(Date.now() / TRAFFIC_BUCKET_MS))

    let renewTimer:  ReturnType<typeof setTimeout>  | undefined
    let bucketTimer: ReturnType<typeof setInterval> | undefined

    /** True when the base map is fetched through stanic-api rather than from OSM. */
    const isProxied = computed(() => sources.value.tiles === TileSource.TomTom && !!sources.value.tile_token)

    /** True when there is a traffic overlay to draw at all — what a toggle asks. */
    const hasTraffic = computed(() => sources.value.traffic && !!sources.value.tile_token)

    const stopTimers = () => {
        if (renewTimer) clearTimeout(renewTimer)
        if (bucketTimer) clearInterval(bucketTimer)

        renewTimer  = undefined
        bucketTimer = undefined
    }

    const startBucket = () => {
        if (bucketTimer) return

        bucketTimer = setInterval(() => {
            trafficBucket.value = Math.floor(Date.now() / TRAFFIC_BUCKET_MS)
        }, TRAFFIC_BUCKET_MS)
    }

    const scheduleRenewal = () => {
        if (renewTimer) clearTimeout(renewTimer)
        renewTimer = undefined

        const expiry = Date.parse(sources.value.tile_token_expires_at)
        if (!sources.value.tile_token || Number.isNaN(expiry)) return

        const wait = Math.max(MIN_RENEW_MS, expiry - Date.now() - RENEW_MARGIN_MS)

        renewTimer = setTimeout(() => { void load() }, wait)
    }

    /*
     * Reads /map-sources and moves the console onto whatever it says.
     *
     * Deliberately silent on every outcome but a 200: no toast, no console line, no
     * Sentry. A stanic-api that has not been deployed with this feature yet is not a
     * fault a person watching a vehicle can do anything about, and the map they are
     * watching carries on working.
     */
    const load = async () => {
        if (!appStore.isLoggedIn()) return

        const { statusCode, data } = await fetchStore.fetch('/map-sources').get().json<IMapSources>()

        if (statusCode.value !== 200 || !data.value) return

        sources.value = { ...defaultSources(), ...data.value }

        scheduleRenewal()
        if (hasTraffic.value) startBucket()
    }

    const reset = () => {
        stopTimers()
        sources.value = defaultSources()
    }

    /*
     * A token outlives the session it was issued for unless somebody throws it away.
     * Signing out — here, or from the 401 the fetch store turns into one — puts the
     * console back to plain OSM, so the tab left on the login screen is not still
     * pulling proxied tiles on the last person's token.
     */
    watch(() => appStore.JWT, (jwt) => { if (!jwt) reset() })

    /*
     * The address of one base tile.
     *
     * OSM's own template unless the base is genuinely proxied — which means both that
     * TomTom was chosen AND that a token arrived. Without the token the proxy would
     * answer 401 and the map would be a grid of holes, so the fallback is the map that
     * has always worked rather than the one that was asked for.
     */
    const baseTileUrl = (z: number, x: number, y: number, dark: boolean): string => {
        if (!isProxied.value) {
            return TILE_URL_TEMPLATE
                .replace('{z}', String(z))
                .replace('{x}', String(x))
                .replace('{y}', String(y))
        }

        return `${API_BASE_URL}/tiles/base/${z}/${x}/${y}.png?t=${encodeURIComponent(sources.value.tile_token)}&style=${tileStyle(dark)}`
    }

    /*
     * The address of one traffic tile, or '' when there is no traffic to draw — which
     * is how the component asks whether to render the layer at all.
     *
     * It goes over WHATEVER the base is: a transparent PNG of the same tile, so a
     * deployment can keep OSM's map and still buy TomTom's traffic.
     */
    const trafficTileUrl = (z: number, x: number, y: number, dark: boolean): string => {
        if (!hasTraffic.value) return ''

        return `${API_BASE_URL}/tiles/traffic/${z}/${x}/${y}.png`
            + `?t=${encodeURIComponent(sources.value.tile_token)}&style=${tileStyle(dark)}&v=${trafficBucket.value}`
    }

    return {
        sources,
        isProxied, hasTraffic,
        load, reset, baseTileUrl, trafficTileUrl,
    }
})
